import { Directive, inject } from '@angular/core';
import { NgpSubmenuTrigger } from 'ng-primitives/menu';

/**
 * `[suiSubmenuTrigger]` — turns a `<button sui-menu-item>` into the parent of a
 * nested submenu, pointing at an `<ng-template>` that holds another
 * `<sui-menu>`. Sits *alongside* `sui-menu-item` on the same button: the item
 * provides the row + `role=menuitem` + roving focus, this directive opens the
 * submenu on hover / ArrowRight (and closes it on ArrowLeft) — `NgpMenuItem`
 * detects it's also a submenu trigger and opens the child instead of closing
 * the menu.
 *
 * Thin pass-through over `NgpSubmenuTrigger` (no input transforms — §12 ② of
 * the CRAFTING-GUIDE). Default placement `right-start` (the primitive's own).
 * `exportAs: 'suiSubmenu'` exposes `isOpen()` for parity with `suiMenuTrigger`.
 *
 * ⚠️ IN-DIALOG LIMITATION — submenus do NOT inherit the top-level menu's
 * dialog stacking. Unlike `NgpMenuTrigger`, `NgpSubmenuTrigger` has **no
 * `container` input** (and ignores `provideMenuConfig`): its overlay always
 * attaches to `document.body`. So a top-level `sui-menu` auto-resolved to a
 * dialog boundary pane stacks correctly, but a *submenu* opened inside a
 * dialog mounts on `body` and renders BEHIND the dialog (the same z-index issue
 * the auto-boundary resolution solves for the parent — see CRAFTING-GUIDE
 * §10/§12). On plain pages submenus work out of the box. Submenus-inside-dialogs
 * need a strategy decision (upstream patch / version bump / custom trigger)
 * before use — deferred until a real consumer appears.
 */
@Directive({
  selector: 'button[sui-menu-item][suiSubmenuTrigger]',
  standalone: true,
  exportAs: 'suiSubmenu',
  hostDirectives: [
    {
      directive: NgpSubmenuTrigger,
      inputs: [
        'ngpSubmenuTrigger: suiSubmenuTrigger',
        'ngpSubmenuTriggerPlacement: suiSubmenuPlacement',
        'ngpSubmenuTriggerDisabled: suiSubmenuTriggerDisabled',
        'ngpSubmenuTriggerOffset: suiSubmenuOffset',
      ],
    },
  ],
})
export class SuiSubmenuTriggerDirective {
  private readonly trigger = inject(NgpSubmenuTrigger);

  /** Current open state of the submenu (the primitive exposes `open` as a signal). */
  isOpen(): boolean {
    return this.trigger.open();
  }
}
